<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="3" rounded="xxl" max-width="800" class="mx-auto">
        <v-card-title class="font-weight-bold text-h5">
          <v-icon large left color="primary">mdi-account-remove</v-icon>
          {{ isEditMode ? 'Edit' : 'View' }} Account Delete Request
          <v-spacer></v-spacer>
          <v-btn
            v-if="!isEditMode"
            color="primary"
            rounded
            @click="toggleEditMode"
          >
            <v-icon left>mdi-pencil</v-icon>
            Edit
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        
        <!-- Loading State -->
        <v-card-text v-if="isLoading" class="text-center py-12">
          <health-loader message="Loading request details..." />
        </v-card-text>
        
        <!-- Content -->
        <v-card-text v-else-if="request">
          <v-form ref="form" v-model="isFormValid" lazy-validation>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.name"
                  :outlined="isEditMode"
                  :readonly="!isEditMode"
                  :filled="!isEditMode"
                  label="Full Name"
                  prepend-inner-icon="mdi-account"
                  :rules="isEditMode ? nameRules : []"
                  :error-messages="errors.name"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.phone"
                  :outlined="isEditMode"
                  :readonly="!isEditMode"
                  :filled="!isEditMode"
                  label="Phone Number"
                  prepend-inner-icon="mdi-phone"
                  :rules="isEditMode ? phoneRules : []"
                  :error-messages="errors.phone"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.status"
                  :outlined="isEditMode"
                  :readonly="!isEditMode"
                  :filled="!isEditMode"
                  :items="statusOptions"
                  item-text="text"
                  item-value="value"
                  label="Status"
                  prepend-inner-icon="mdi-flag"
                  :error-messages="errors.status"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6" v-if="!isEditMode">
                <v-text-field
                  :value="formatDate(request.created_at)"
                  readonly
                  filled
                  label="Created At"
                  prepend-inner-icon="mdi-calendar"
                ></v-text-field>
              </v-col>
              <v-col cols="12" v-if="!isEditMode && request.updated_at">
                <v-text-field
                  :value="formatDate(request.updated_at)"
                  readonly
                  filled
                  label="Last Updated"
                  prepend-inner-icon="mdi-clock"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
          
          <v-divider class="my-4" v-if="isEditMode"></v-divider>
          
          <!-- Edit Mode Actions -->
          <div class="text-center" v-if="isEditMode">
            <v-btn
              :loading="isSaving"
              :disabled="!isFormValid"
              rounded
              color="primary"
              size="large"
              @click="saveChanges"
              class="px-8"
            >
              <v-icon left>mdi-content-save</v-icon>
              Save Changes
            </v-btn>
            <v-btn
              rounded
              color="grey"
              text
              class="ml-3"
              @click="cancelEdit"
            >
              Cancel
            </v-btn>
          </div>
          
          <!-- View Mode Actions -->
          <div class="text-center" v-else>
            <v-btn
              rounded
              color="grey"
              @click="$router.push('/account-delete-requests')"
              class="px-6"
            >
              <v-icon left>mdi-arrow-left</v-icon>
              Back to List
            </v-btn>
          </div>
        </v-card-text>
        
        <!-- Error State -->
        <v-card-text v-else class="text-center py-12">
          <v-icon size="80" color="error" class="mb-4">mdi-alert-circle</v-icon>
          <h3 class="text-h6 font-weight-bold error--text mb-2">Error Loading Request</h3>
          <p class="text-body-1 mb-4">{{ errorMessage }}</p>
          <v-btn
            color="primary"
            rounded
            @click="loadRequest"
            class="px-6"
          >
            <v-icon left>mdi-refresh</v-icon>
            Retry
          </v-btn>
        </v-card-text>
      </v-card>
      
      <!-- Success Dialog -->
      <v-dialog v-model="successDialog" max-width="450" persistent>
        <v-card rounded="xxl">
          <v-card-text class="text-center py-6">
            <v-icon size="80" color="success" class="mb-4">mdi-check-circle</v-icon>
            <h2 class="text-h5 font-weight-bold success--text mb-2">Success!</h2>
            <p class="text-body-1 mb-4">Account delete request has been updated successfully.</p>
            <v-btn
              color="success"
              rounded
              @click="handleSuccess"
              class="px-6"
            >
              OK
            </v-btn>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Error Dialog -->
      <v-dialog v-model="errorDialog.show" max-width="450" persistent>
        <v-card rounded="xxl">
          <v-card-text class="text-center py-6">
            <v-icon size="80" color="error" class="mb-4">mdi-alert-circle</v-icon>
            <h2 class="text-h5 font-weight-bold error--text mb-2">Error!</h2>
            <p class="text-body-1 mb-4">{{ errorDialog.message }}</p>
            <v-btn
              color="error"
              rounded
              @click="errorDialog.show = false"
              class="px-6"
            >
              OK
            </v-btn>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "AccountDeleteRequestDetail",
  data() {
    return {
      isFormValid: false,
      isEditMode: false,
      isLoading: false,
      isSaving: false,
      request: null,
      originalData: null,
      errorMessage: "",
      form: {
        name: "",
        phone: "",
        status: "1",
      },
      successDialog: false,
      errorDialog: {
        show: false,
        message: "",
      },
      errors: {},
      statusOptions: [
        { text: "Pending", value: "1" },
        { text: "Complete", value: "2" },
      ],
      nameRules: [
        v => !!v || "Name is required",
        v => (v && v.length >= 2) || "Name must be at least 2 characters",
        v => (v && v.length <= 100) || "Name must be less than 100 characters",
      ],
      phoneRules: [
        v => !!v || "Phone number is required",
        v => (v && v.length >= 10) || "Phone number must be at least 10 digits",
        v => (v && v.length <= 15) || "Phone number must be less than 15 digits",
        v => /^[\d+\-\s()]+$/.test(v) || "Phone number must contain only digits, +, -, spaces, and parentheses",
      ],
    };
  },
  computed: {
    requestId() {
      return this.$route.params.id;
    },
  },
  mounted() {
    this.loadRequest();
    
    // Check if we're in edit mode from route query
    if (this.$route.query.edit === "true") {
      this.isEditMode = true;
    }
  },
  methods: {
    async loadRequest() {
      this.isLoading = true;
      this.errorMessage = "";
      
      try {
        const response = await axios.get(`/account_delete_request/${this.requestId}`);
        this.request = response.data.data;
        this.populateForm();
        this.storeOriginalData();
      } catch (error) {
        console.error("Error loading account delete request:", error);
        if (error.response && error.response.status === 404) {
          this.errorMessage = "Account delete request not found.";
        } else {
          this.errorMessage = "Failed to load account delete request details.";
        }
      } finally {
        this.isLoading = false;
      }
    },
    
    populateForm() {
      if (this.request) {
        this.form = {
          name: this.request.name || "",
          phone: this.request.phone || "",
          status: this.request.status || "1",
        };
      }
    },
    
    storeOriginalData() {
      this.originalData = { ...this.form };
    },
    
    toggleEditMode() {
      this.isEditMode = true;
      this.$router.replace({ query: { edit: "true" } });
    },
    
    cancelEdit() {
      this.isEditMode = false;
      this.form = { ...this.originalData };
      this.errors = {};
      this.$router.replace({ query: {} });
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    
    async saveChanges() {
      // Validate form
      if (!this.$refs.form.validate()) {
        return;
      }

      this.isSaving = true;
      this.errors = {};

      try {
        const response = await axios.put(`/account_delete_request/${this.requestId}`, this.form);
        this.request = response.data.data;
        this.storeOriginalData();
        this.isEditMode = false;
        this.$router.replace({ query: {} });
        this.successDialog = true;
      } catch (error) {
        console.error("Error updating account delete request:", error);
        
        if (error.response && error.response.status === 422) {
          // Validation errors
          this.errors = error.response.data.errors || {};
          this.errorDialog.message = "Please check the form for validation errors.";
        } else {
          this.errorDialog.message = "An unexpected error occurred. Please try again.";
        }
        this.errorDialog.show = true;
      } finally {
        this.isSaving = false;
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return "";
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    
    handleSuccess() {
      this.successDialog = false;
    },
  },
};
</script>

<style scoped>
.v-card {
  margin-top: 20px;
}

.success--text {
  color: #4caf50 !important;
}

.error--text {
  color: #f44336 !important;
}
</style>